import { z } from 'zod';

/**
 * Validation regex patterns
 */
const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
const COLOR_HEX_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

/**
 * Create dynamic Zod schema based on validation rules
 * Uses native Zod validations applied directly on the schema
 * @returns {z.ZodType}
 */
export const createValidationSchema = () => {
  const baseSchema = z.object({
    id: z.string().min(1, 'batchProcessing.id.required'),
    lib: z.string().optional(),
    type: z.string().optional(),
    value: z.any(),
  }).refine(
    (data) => {
      // Skip validation if id or type is missing
      if (!data.id || !data.type) return true;

      const { type, value } = data;

      if (type === 'singleAction') return true;

      // Text and textarea validation: non-empty string after trim
      if (type === 'text' || type === 'textarea') {
        return value && typeof value === 'string' && value.trim() !== '';
      }

      return true;
    },
    {
      message: 'batchProcessing.text.required',
      path: ['value'],
    },
  )
    .refine(
      (data) => {
        if (!data.id || !data.type || data.type !== 'date') return true;

        const { value } = data;
        if (!value) return false;

        const date = new Date(value);
        return date instanceof Date && !Number.isNaN(date.getTime());
      },
      {
        message: 'batchProcessing.date.required',
        path: ['value'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.type || data.type !== 'time') return true;

        const { value } = data;
        return value && typeof value === 'string' && TIME_REGEX.test(value.trim());
      },
      {
        message: 'batchProcessing.time.required',
        path: ['value'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.type || data.type !== 'color') return true;

        const { value } = data;
        return value && typeof value === 'string' && COLOR_HEX_REGEX.test(value.trim());
      },
      {
        message: 'batchProcessing.color.required',
        path: ['value'],
      },
    )
    .refine(
      (data) => {
        if (!data.id || !data.type) return true;

        const { type, value } = data;
        const selectTypes = ['selectEnum', 'selectClass', 'selectOrCreateForm'];
        if (!selectTypes.includes(type)) return true;

        return value && typeof value === 'string' && value.trim() !== '';
      },
      {
        message: 'batchProcessing.select.required',
        path: ['value'],
      },
    );

  return z.object({
    actions: z.array(baseSchema).optional().default([]),
  });
};

/**
 * Find the non-null type and its data from an API item
 * @param {ApiManagerTableFiltersSetAttributMultipleItem} item - The API item with all possible types
 * @returns {{ foundType: string | null, typeData: any }} The found type and its data, or nulls if not found
 */
const findNonNullTypeAndData = (item) => {
  if (item.text) {
    return { foundType: 'text', typeData: item.text };
  }
  if (item.date) {
    return { foundType: 'date', typeData: item.date };
  }
  if (item.time) {
    return { foundType: 'time', typeData: item.time };
  }
  if (item.color) {
    return { foundType: 'color', typeData: item.color };
  }
  if (item.textarea) {
    return { foundType: 'textarea', typeData: item.textarea };
  }
  if (item.selectEnum) {
    return { foundType: 'selectEnum', typeData: item.selectEnum };
  }
  if (item.selectClass) {
    return { foundType: 'selectClass', typeData: item.selectClass };
  }
  if (item.selectOrCreateForm) {
    return { foundType: 'selectOrCreateForm', typeData: item.selectOrCreateForm };
  }
  if (item.singleAction) {
    return { foundType: 'singleAction', typeData: item.singleAction };
  }

  return { foundType: null, typeData: null };
};

/**
 * TODO LOIC: Some 'any' types here to prepare for demo in a short time but need to implement proper types
 * Build a transformed item from type and data
 * @param {string} foundType - The type found (text, date, time, etc.)
 * @param {any} typeData - The data for that type
 * @returns {TransformedBatchActionOption | null} The transformed item or null if invalid
 */
const buildTransformedItem = (foundType, typeData) => {
  if (!foundType || !typeData) {
    return null;
  }

  // Build the complete object based on the type with explicit typing
  if (foundType === 'selectEnum') {
    /** @type {{id: string, lib: string, optGroup: string, selectOptions: ApiManagerTableFiltersSetAttributeMultipleSelectEnumOption[]}} */
    const enumData = /** @type {any} */ (typeData);
    /** @type {ApiManagerTableFiltersSetAttributeMultipleSelectEnum} */
    return {
      id: enumData.id,
      lib: enumData.lib,
      optGroup: enumData.optGroup || null,
      type: 'selectEnum',
      selectOptions: enumData.selectOptions,
    };
  }

  if (foundType === 'selectClass') {
    /** @type {{id: string, lib: string, optGroup: string, selectOptionsUrl: string}} */
    const classData = /** @type {any} */ (typeData);
    /** @type {ApiManagerTableFiltersSetAttributeMultipleSelectClass} */
    return {
      id: classData.id,
      lib: classData.lib,
      optGroup: classData.optGroup || null,
      type: 'selectClass',
      selectOptionsUrl: classData.selectOptionsUrl,
    };
  }

  if (foundType === 'selectOrCreateForm') {
    /** @type {{id: string, lib: string, optGroup: string, formUrl: string}} */
    const formData = /** @type {any} */ (typeData);
    /** @type {ApiManagerTableFiltersSetAttributeMultipleSelectOrCreateForm} */
    return {
      id: formData.id,
      lib: formData.lib,
      optGroup: formData.optGroup || null,
      type: 'selectOrCreateForm',
      formUrl: formData.formUrl,
    };
  }

  // For simple types: text, date, time, color, textarea
  /** @type {ApiManagerTableFiltersSetAttributeMultipleString} */
  return {
    id: typeData.id,
    lib: typeData.lib,
    optGroup: typeData.optGroup || null,
    type: /** @type {'text' | 'date' | 'time' | 'color' | 'textarea' | 'singleAction'} */ (foundType),
  };
};

/**
 * Transform setAttributeMultiple from new API format to TransformedBatchActionOption format
 * New format: [{text: null, date: null, time: {...}, ...}] where one type is non-null
 * Expected format: [{id: '...', lib: '...', optGroup: '...', type: 'time', ...}]
 * @param {Array<ApiManagerTableFiltersSetAttributMultipleItem> | undefined} apiData - The setAttributeMultiple data from API (new format with all types as properties)
 * @returns {TransformedBatchActionOption[]} Transformed data in TransformedBatchActionOption format
 */
export const transformSetAttributeMultiple = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    return [];
  }

  // @ts-ignore - TypeScript cannot infer the union type correctly, but the transformation is type-safe
  return /** @type {TransformedBatchActionOption[]} */ (apiData
    .map((item) => {
      const { foundType, typeData } = findNonNullTypeAndData(item);
      return buildTransformedItem(foundType, typeData);
    })
    .filter((item) => item !== null));
};
